import React, { useState, useMemo } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import { useAuth, selectCanUpdateListings } from "context/AuthContext";
import RejectListingModal from "modals/RejectListingModal";
import { UncontrolledTooltip } from "reactstrap";

const Header = ({ journeyDetails, handleToggleAccount, goBack, type, loadingAction, isLoading, changedPaths = [] }) => {
  const { t } = useTranslation();
  const { permission } = useAuth();
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const availableStatuses = useMemo(
    () =>
      Object.keys(helpers.getStatusArray("listing")).filter(
        (s) =>
          s !== "draft" &&
          s !== "cancellation_requested" &&
          s !== "cancelled" &&
          !(
            (type?.toLowerCase() === "traveler" ||
              journeyDetails?.type?.toLowerCase() === "traveler") &&
            s === "pending"
          )
      ),
    [type, journeyDetails]
  );

  if (!journeyDetails) return null;

  const canUpdateListing = selectCanUpdateListings(permission);
  const from = journeyDetails.fromPlace?.name || "-";
  const to = journeyDetails.toPlace?.name || "-";

  const details =
    journeyDetails?.baggageDetails || journeyDetails?.packageDetails;

  let price = "-";

  if (details?.totalPrice || details?.price) {
    const symbol =
      details?.currency?.symbol && details.currency.symbol.trim() !== ""
        ? details.currency.symbol
        : helpers.DEFAULT_CURRENCY;

    price = `${symbol}${details.totalPrice || details.price}`;
  }

  const currentStatus = helpers.normalizeOrderStatus(journeyDetails.status);
  const isExpired = helpers.isExpired(journeyDetails) || currentStatus === "expired";

  const statusClass = helpers.getStatusClass(currentStatus, "listing");

  const journeyDate = journeyDetails.date
    ? helpers.getFormattedDate(journeyDetails.date, t)
    : "-";
  const isDeleted = journeyDetails?.isDeleted;

  return (
    <>
    <Card className="shadow-sm mb-3">
      <CardBody className="py-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-2">
          <h5 className="mb-3 mb-md-0 font-weight-bold text-lg mt-0 text-center text-md-left">
            {t("page.allListings.listingDetails", "Listing Details")}
          </h5>

          <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-end">
            {isExpired ? (
              <span className="expired-listing-pill mr-2 mb-2 mb-md-0">
                {t("page.userListings.status.expired", "Expired")}
              </span>
            ) : (
              <>
                <UncontrolledDropdown>
                  <DropdownToggle
                    caret
                    size="sm"
                    className={`status-dropdown ${statusClass} mb-2 mb-md-0`}
                    id={`statusDropdown-${journeyDetails._id}`}
                    disabled={!canUpdateListing || isDeleted || loadingAction}
                  >
                    {t(
                      `page.userListings.status.${currentStatus}`,
                      helpers.capitalizeFirstLetter((currentStatus || "").replace(/_/g, " "))
                    )}
                  </DropdownToggle>

                  <DropdownMenu>
                    {availableStatuses.map((status) => (
                      <DropdownItem
                        key={status}
                        disabled={status === currentStatus || !canUpdateListing}
                        onClick={() => {
                          if (canUpdateListing) {
                            if (status === "rejected") {
                              setIsRejectModalOpen(true);
                            } else {
                              handleToggleAccount(journeyDetails, status, type);
                            }
                          }
                        }}
                      >
                        {t(
                          `page.userListings.status.${status}`,
                          helpers.capitalizeFirstLetter((status || "").replace(/_/g, " "))
                        )}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>

                {(isDeleted || !canUpdateListing) && !loadingAction && (
                  <UncontrolledTooltip
                    target={`statusDropdown-${journeyDetails._id}`}
                  >
                    {isDeleted ? t("tooltip.alreadyDeleted", "Listing already deleted") : t("tooltip.noPermission", "Permission denied")}
                  </UncontrolledTooltip>
                )}
              </>
            )}

            <Button
              color="secondary"
              className="ml-2 mb-2 mb-md-0"
              size="sm"
              onClick={goBack}
            >
              {t("page.messages.back", "Back")}
            </Button>
          </div>
        </div>
        <Row className="align-items-center text-center mt-4">
          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">
              {t("page.listing.from", "From")}
            </small>
            <div className="text-sm font-weight-bold">
              <span className={helpers.getHighlightClass("fromPlace.name", changedPaths)}>{from}</span>
            </div>
          </Col>

          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">{t("page.listing.to", "To")}</small>
            <div className="text-sm font-weight-bold">
              <span className={helpers.getHighlightClass("toPlace.name", changedPaths)}>{to}</span>
            </div>
          </Col>

          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">
              {t("page.listing.date", "Date")}
            </small>
            <div className="text-sm font-weight-bold">
              <span className={helpers.getHighlightClass("date", changedPaths)}>{journeyDate}</span>
            </div>
          </Col>

          <Col xs="6" md="3" className="mb-3 mb-md-0">
            <small className="text-muted d-block">
              {t("table.headers.price", "Price")}
            </small>
            <div className="text-sm font-weight-bold">
              <span className={helpers.getHighlightClass(
                type === "sender" 
                  ? ["packageDetails.totalPrice", "packageDetails.price"] 
                  : ["baggageDetails.totalPrice", "baggageDetails.price"], 
                changedPaths
              )}>
                {price}
              </span>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
    <RejectListingModal
      isOpen={isRejectModalOpen}
      toggle={() => setIsRejectModalOpen(false)}
      onConfirm={async (adminRemarks) => {
        await handleToggleAccount(journeyDetails, "rejected", type, {
          adminRemarks: adminRemarks,
        });
        setIsRejectModalOpen(false);
      }}
      isLoading={isLoading}
    />
    </>
  );
};

export default Header;
